import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { api, IMAGES, unsplashSrcSet } from "../lib/api";
import { MapPin, Image as ImageIcon, Images } from "lucide-react";
import Lightbox from "./Lightbox";
import { Reveal, MaskedLine } from "./motion/Reveal";

const CATEGORY_KEYS = ["all", "Groepenkasten", "Storingen", "Verlichting", "Renovaties", "Intercom", "Data & Netwerk"];

const placeholders = [
  { id: "p1", title_key: "Groepenkast vervanging", location: "Amsterdam-Oost", category: "Groepenkasten", description: "Volledige vervanging van een verouderde meterkast, uitgebreid met aardlekautomaten en extra groepen.", images: [IMAGES.groepenkast] },
  { id: "p2", title_key: "LED verlichting kantoor", location: "Amsterdam-Zuid", category: "Verlichting", description: "Complete herinrichting kantoorverlichting met dimbare LED-armaturen en aanwezigheidssensoren.", images: [IMAGES.verlichting] },
  { id: "p3", title_key: "Renovatie appartement", location: "Haarlem", category: "Renovaties", description: "Nieuwe elektrische installatie tijdens totale renovatie, inclusief bedrading en schakelmateriaal.", images: [IMAGES.storingen] },
  { id: "p4", title_key: "Storing verhelpen", location: "Amstelveen", category: "Storingen", description: "Diagnose en oplossing van een terugkerende storing in het aardlek­systeem van een woning.", images: [IMAGES.hero] },
];

function normalize(p) {
  const images = (p.images && p.images.length) ? p.images : (p.image_data_url ? [p.image_data_url] : []);
  return { ...p, images };
}

export default function Projects() {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [active, setActive] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/projects")
      .then((r) => setProjects((r.data || []).map(normalize)))
      .catch(() => setProjects([]))
      .finally(() => setLoaded(true));
  }, []);

  const list = loaded && projects.length > 0
    ? projects.map((p) => ({ ...p, title: p.title || p.title_key || "" }))
    : placeholders.map((p) => ({ ...normalize(p), title: p.title_key }));

  const filtered = useMemo(
    () => (active === "all" ? list : list.filter((p) => (p.category || "").toLowerCase() === active.toLowerCase())),
    [list, active]
  );

  const availableCats = useMemo(() => {
    if (!loaded || projects.length === 0) return CATEGORY_KEYS;
    const set = new Set(projects.map((p) => (p.category || "").toLowerCase()));
    return ["all", ...CATEGORY_KEYS.filter((c) => c !== "all" && set.has(c.toLowerCase()))];
  }, [loaded, projects]);

  const openLightbox = (project, index = 0) => {
    if (project.images && project.images.length > 0) {
      setLightbox({ project, index });
    }
  };

  return (
    <section id="projecten" data-testid="projects-section" className="section bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="mb-10 md:mb-14 grid lg:grid-cols-12 gap-6 items-end">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="flex items-center gap-3 mb-4">
                <span className="accent-bar" />
                <span className="text-[#FF5A1F] uppercase font-bold tracking-widest text-xs">
                  {t("projects.eyebrow")}
                </span>
              </div>
            </Reveal>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#0A1128] mb-5 leading-[1.02]">
              <span className="block overflow-hidden"><MaskedLine text={t("projects.title_1")} wordDelay={0.06} /></span>
              <span className="block overflow-hidden"><MaskedLine text={t("projects.title_2")} wordDelay={0.06} delay={0.15} /></span>
            </h2>
            <Reveal delay={0.15}>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl">
                {t("projects.subtitle")}
                {loaded && projects.length === 0 && (
                  <span className="block text-sm text-slate-500 mt-2 italic">
                    {t("projects.placeholder_note")}
                  </span>
                )}
              </p>
            </Reveal>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8 md:mb-10">
          {availableCats.map((c) => {
            const isActive = active === c;
            const label = c === "all" ? t("projects.categories.all") : t(`projects.categories.${c}`);
            return (
              <button
                key={c}
                onClick={() => setActive(c)}
                data-testid={`projects-filter-${c.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                className={`px-4 md:px-5 py-2 md:py-2.5 text-xs md:text-sm font-bold uppercase tracking-wider border-2 transition-all ${
                  isActive
                    ? "bg-[#0A1128] text-white border-[#0A1128]"
                    : "bg-white text-slate-600 border-slate-200 hover:border-[#0A1128] hover:text-[#0A1128]"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        {filtered.length === 0 ? (
          <div className="border border-slate-200 p-16 text-center text-slate-500">
            <ImageIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            {t("projects.empty")}
          </div>
        ) : (
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px 0px" }}
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1 } } }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
          >
            {filtered.map((p, idx) => {
              const cover = p.images[0] || IMAGES.hero;
              const count = p.images.length;
              return (
                <motion.article
                  key={p.id || idx}
                  data-testid={`project-card-${idx}`}
                  variants={{ hidden: { opacity: 0, y: 40 }, show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } } }}
                  whileHover={{ y: -4 }}
                  onClick={() => openLightbox(p, 0)}
                  className="group relative overflow-hidden border border-slate-200 bg-white hover:border-[#0A1128] hover:shadow-[8px_8px_0_0_rgba(10,17,40,0.08)] transition-[border-color,box-shadow] cursor-pointer"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-slate-100 relative">
                    <img
                      src={cover}
                      srcSet={unsplashSrcSet(cover, [400, 600, 800])}
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                      alt={p.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                      decoding="async"
                      width="800"
                      height="600"
                      style={{ transform: "translateZ(0)", backfaceVisibility: "hidden" }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    {count > 1 && (
                      <div className="absolute top-3 right-3 flex items-center gap-1 bg-[#0A1128]/85 backdrop-blur-sm text-white text-xs font-bold uppercase tracking-wider px-2.5 py-1.5">
                        <Images className="w-3.5 h-3.5" />
                        <span>{count}</span>
                      </div>
                    )}
                    <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <span className="text-[10px] uppercase tracking-widest font-bold text-white bg-[#FF5A1F] px-2 py-1">
                        {t("projects.view_photos")}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    {p.category && (
                      <span className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold">
                        {t(`projects.categories.${p.category}`, { defaultValue: p.category })}
                      </span>
                    )}
                    <h3 className="font-display text-xl md:text-2xl text-[#0A1128] mt-2 mb-2 leading-tight">{p.title}</h3>
                    {p.location && (
                      <div className="flex items-center gap-1.5 text-sm text-slate-500 mb-3">
                        <MapPin className="w-4 h-4" /> {p.location}
                      </div>
                    )}
                    <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">{p.description}</p>
                  </div>
                </motion.article>
              );
            })}
          </motion.div>
        )}
      </div>

      {lightbox && (
        <Lightbox
          images={lightbox.project.images}
          index={lightbox.index}
          title={`${lightbox.project.title}${lightbox.project.location ? ` — ${lightbox.project.location}` : ""}`}
          onClose={() => setLightbox(null)}
          onIndex={(i) => setLightbox({ ...lightbox, index: i })}
        />
      )}
    </section>
  );
}
